import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, ArrowRightIcon, CheckCircle2Icon, CheckIcon, Loader2Icon, MailCheckIcon } from 'lucide-react';
import { AuthLayout } from '../../layouts/AuthLayout';
import { useAuth } from '../../contexts/AuthContext';
import { updateOrganization } from '../../services/organizationService';
import { SECTOR_OPTIONS } from '../../data/sectors';
import { BrandLogo } from '../../components/shared/BrandLogo';
import { LogoUploader } from '../../components/shared/LogoUploader';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Checkbox } from '../../components/ui/Checkbox';
interface FormState {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  repeatPassword: string;
  companyName: string;
  sector: string;
  primaryColor: string;
  secondaryColor: string;
}
const INITIAL: FormState = {
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  repeatPassword: '',
  companyName: '',
  sector: '',
  primaryColor: '#3D8BFF',
  secondaryColor: '#0B1F4D'
};
const STEPS = ['Tu cuenta', 'Tu empresa', 'Logotipo', 'Colores', 'Sector'];
const CREATING_STEPS = ['Creando tu cuenta', 'Creando el espacio de trabajo de tu empresa', 'Preparando los estados iniciales del pipeline'];
const PRESET_COLORS = ['#3D8BFF', '#0B1F4D', '#16A34A', '#7C3AED', '#F97316', '#0EA5E9'];
export function Register() {
  const {
    register,
    loading
  } = useAuth();
  const navigate = useNavigate();
  const [values, setValues] = useState<FormState>(INITIAL);
  const [logo, setLogo] = useState<string | undefined>(undefined);
  const [terms, setTerms] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [step, setStep] = useState(0);
  const [creating, setCreating] = useState(false);
  /** La cuenta se ha creado pero necesita confirmarse por email. */
  const [confirmEmail, setConfirmEmail] = useState(false);
  function set(key: keyof FormState, value: string) {
    setValues(prev => ({
      ...prev,
      [key]: value
    }));
    setErrors(prev => ({
      ...prev,
      [key]: ''
    }));
  }
  function validateStep(index: number): boolean {
    const next: Record<string, string> = {};
    if (index === 0) {
      if (!values.firstName.trim()) next.firstName = 'Introduce tu nombre.';
      if (!values.lastName.trim()) next.lastName = 'Introduce tus apellidos.';
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) next.email = 'Introduce un email válido.';
      if (values.password.length < 8) next.password = 'Usa al menos 8 caracteres.';
      if (values.password !== values.repeatPassword) next.repeatPassword = 'Las contraseñas no coinciden.';
      if (!terms) next.terms = 'Debes aceptar los términos.';
    }
    if (index === 1 && !values.companyName.trim()) next.companyName = 'Introduce el nombre de tu empresa o tu nombre.';
    if (index === 4 && !values.sector) next.sector = 'Selecciona un sector.';
    setErrors(next);
    return Object.keys(next).length === 0;
  }
  async function finish() {
    if (!validateStep(4)) return;
    setCreating(true);
    try {
      const session = await register({
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        password: values.password,
        companyName: values.companyName,
        sector: values.sector,
        primaryColor: values.primaryColor,
        secondaryColor: values.secondaryColor,
        logoUrl: logo
      });

      // Sin sesión: la cuenta debe confirmarse por email y el espacio de
      // trabajo se creará en el primer acceso.
      if (!session) {
        setCreating(false);
        setConfirmEmail(true);
        return;
      }
      await updateOrganization(session.organizationId, {
        logoUrl: logo,
        brand: {
          primaryColor: values.primaryColor,
          secondaryColor: values.secondaryColor,
          fontFamily: 'Inter',
          logoUrl: logo
        }
      });
      navigate('/app/dashboard');
    } catch (err) {
      setCreating(false);
      setStep(0);
      setErrors({
        email: err instanceof Error ? err.message : 'No se ha podido crear la cuenta.'
      });
    }
  }
  if (confirmEmail) {
    return <AuthLayout title="Confirma tu correo" subtitle={`Hemos enviado un enlace de confirmación a ${values.email}.`} footer={<span className="text-muted-foreground" data-id="element-1641" data-mp="17ik668khl9vj">
            ¿Ya lo has confirmado?{' '}
            <Link to="/login" className="font-medium text-primary hover:underline" data-id="element-1642" data-mp="1mfuq86b73o2v">
              Iniciar sesión
            </Link>
          </span>} data-id="element-1640" data-mp="1ch59igv1c9o7">
        <div className="space-y-5 text-sm leading-relaxed text-muted-foreground" data-id="element-1643" data-mp="1hgrn4rerzrxj">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10" data-id="element-1644" data-mp="3o7h89id408n7">
            <MailCheckIcon className="h-6 w-6 text-primary" aria-hidden="true" data-id="element-1645" data-mp="3j8wi6brkn5ch" />
          </span>
          <p data-id="element-1646" data-mp="1yqvh7kkvqi5x">
            Abre el enlace del correo para activar tu cuenta. Al iniciar sesión
            por primera vez crearemos automáticamente el espacio de trabajo de{' '}
            <strong className="text-foreground" data-id="element-1647" data-mp="1p0ize04g933h">{values.companyName}</strong> con
            tu logotipo, tus colores, los estados del embudo y tu primera landing.
          </p>
          <p data-id="element-1648" data-mp="1fzb0pgy0u08v">
            Si no lo encuentras, revisa la carpeta de spam. El enlace caduca en
            24 horas.
          </p>
          <Link to="/login" data-id="element-1649" data-mp="1k1pj5zxs2xzm">
            <Button size="lg" className="w-full" data-id="element-1650" data-mp="1k1zabquk7b6w">
              Ir a iniciar sesión
            </Button>
          </Link>
        </div>
      </AuthLayout>;
  }
  if (creating) {
    return <AuthLayout title="Estamos creando tu CRM" subtitle={`Espacio de trabajo de ${values.companyName}`} data-id="element-1651" data-mp="1b0rctazvaedv">
        <ul className="space-y-4" data-id="element-1652" data-mp="30ffpzega80cy">
          {CREATING_STEPS.map((label, index) => <li key={label} className="flex items-center gap-3 text-sm" data-id="element-1653" data-mp="35e95fcf4q1fr" data-mpi={index}>
              {index === 0 ? <CheckCircle2Icon className="h-5 w-5 text-primary" aria-hidden="true" data-id="element-1654" data-mp="33xd7swmct3r6" /> : <Loader2Icon className="h-5 w-5 animate-spin text-muted-foreground" aria-hidden="true" data-id="element-1655" data-mp="2yxqruff559eo" />}
              <span className="text-foreground" data-id="element-1656" data-mp="2tyo0fxs331p6">{label}</span>
            </li>)}
        </ul>
      </AuthLayout>;
  }
  const stepper = <ol className="mb-9 flex items-center gap-2" aria-label="Progreso del registro" data-id="element-1657" data-mp="2p0cp1im54j4u">
      {STEPS.map((label, index) => <li key={label} className="flex flex-1 items-center gap-2" data-id="element-1658" data-mp="3mma3n4xeg4bi" data-mpi={index}>
          <span className={`flex h-7 w-7 flex-none items-center justify-center rounded-full text-[11px] font-semibold transition-colors ${index < step ? 'bg-primary/10 text-primary' : index === step ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`} data-id="element-1659" data-mp="3hoi6nwhwds0g">
            {index < step ? <CheckIcon className="h-3.5 w-3.5" data-id="element-1660" data-mp="985bqax4kaa4" /> : index + 1}
          </span>
          {index < STEPS.length - 1 && <span className={`h-px flex-1 ${index < step ? 'bg-primary/40' : 'bg-border'}`} aria-hidden="true" data-id="element-1661" data-mp="4h6sp3kr4y36" />}
        </li>)}
    </ol>;
  return <AuthLayout title={step === 0 ? 'Crea el CRM de tu empresa' : `Paso ${step + 1} · ${STEPS[step]}`} subtitle={step === 0 ? 'Tu espacio de trabajo se crea automáticamente y solo tu equipo podrá verlo.' : 'Personaliza el espacio de trabajo de tu empresa. Podrás cambiarlo cuando quieras.'} footer={step === 0 ? <span className="text-muted-foreground" data-id="element-1663" data-mp="qrov7fwdd0a4">
            ¿Ya tienes cuenta?{' '}
            <Link to="/login" className="font-medium text-primary hover:underline" data-id="element-1664" data-mp="vofai10cnruh">
              Iniciar sesión
            </Link>
          </span> : undefined} data-id="element-1662" data-mp="crxdp56n91rd">
      {stepper}

      <div className="space-y-5" data-id="element-1665" data-mp="14h8x6ji4ltid">
        {step === 0 && <>
            <div className="grid gap-4 sm:grid-cols-2" data-id="element-1666" data-mp="19gltrvysuey5">
              <Input id="firstName" label="Nombre" value={values.firstName} error={errors.firstName} onChange={e => set('firstName', e.target.value)} data-id="element-1667" data-mp="19fah16ndlwuw" />
              <Input id="lastName" label="Apellidos" value={values.lastName} error={errors.lastName} onChange={e => set('lastName', e.target.value)} data-id="element-1668" data-mp="1jejh8h8u47pp" />
            </div>
            <Input id="email" label="Email" type="email" autoComplete="email" value={values.email} error={errors.email} onChange={e => set('email', e.target.value)} data-id="element-1669" data-mp="24rufojxp6wfb" />
            <div className="grid gap-4 sm:grid-cols-2" data-id="element-1670" data-mp="2dpd1hqj61es9">
              <Input id="password" label="Contraseña" type="password" autoComplete="new-password" value={values.password} error={errors.password} onChange={e => set('password', e.target.value)} data-id="element-1671" data-mp="28rbjza1e1niw" />
              <Input id="repeatPassword" label="Repetir contraseña" type="password" autoComplete="new-password" value={values.repeatPassword} error={errors.repeatPassword} onChange={e => set('repeatPassword', e.target.value)} data-id="element-1672" data-mp="1h05ioq9xljs1" />
            </div>
            <div data-id="element-1673" data-mp="1pzshr5f6udhx">
              <Checkbox id="terms" label="Acepto los términos y la política de privacidad." checked={terms} onChange={e => {
            setTerms(e.target.checked);
            setErrors(prev => ({
              ...prev,
              terms: ''
            }));
          }} data-id="element-1674" data-mp="1pzacjjajsoxv" />
              {errors.terms && <p role="alert" className="mt-2 text-sm font-medium text-red-600" data-id="element-1675" data-mp="38xymeqah1xms">
                  {errors.terms}
                </p>}
            </div>
          </>}

        {step === 1 && <Input id="companyName" label="Nombre de empresa o persona" placeholder="Grupo Empresarial XYZ o Carlos Martínez" value={values.companyName} error={errors.companyName} onChange={e => set('companyName', e.target.value)} data-id="element-1676" data-mp="2zzwb9hx0xols" />}

        {step === 2 && <>
            <LogoUploader value={logo} onChange={setLogo} data-id="element-1677" data-mp="jpwjpzc3kyb" />
            <div className="rounded-xl border border-border bg-surface p-4" data-id="element-1678" data-mp="j6hqi4zdcoi">
              <p className="mb-3 text-xs font-medium uppercase tracking-wider text-muted-foreground" data-id="element-1679" data-mp="3io9r4f1zcauq">
                Vista previa
              </p>
              <div className="flex items-center gap-3 rounded-lg bg-background px-4 py-3" data-id="element-1680" data-mp="3nq8uhoxpz7ro">
                <BrandLogo src={logo} size={22} data-id="element-1681" data-mp="27c8tsbr1ve3d" />
                <span className="text-sm text-muted-foreground" data-id="element-1682" data-mp="2caaet281dnhh">
                  {values.companyName || 'Tu empresa'}
                </span>
              </div>
            </div>
            <p className="text-xs text-muted-foreground" data-id="element-1683" data-mp="2ryyvydpzc7pb">
              Puedes continuar sin logotipo y añadirlo más adelante desde
              Configuración → Identidad de empresa.
            </p>
          </>}

        {step === 3 && <>
            <div className="grid gap-4 sm:grid-cols-2" data-id="element-1684" data-mp="2i72vwx4c27ig">
              <Input id="primary" label="Color principal" type="color" value={values.primaryColor} onChange={e => set('primaryColor', e.target.value)} data-id="element-1685" data-mp="248d0dlx5wfw7" />
              <Input id="secondary" label="Color secundario" type="color" value={values.secondaryColor} onChange={e => set('secondaryColor', e.target.value)} data-id="element-1686" data-mp="28bswmh5bke8d" />
            </div>
            <div data-id="element-1687" data-mp="1zakwdtj8t3bn">
              <p className="mb-2 text-sm font-medium text-foreground" data-id="element-1688" data-mp="1za1rbdeoy9f1">Sugerencias</p>
              <div className="flex flex-wrap gap-2" data-id="element-1689" data-mp="3oq120yxyk6ws">
                {PRESET_COLORS.map(color => <button key={color} type="button" aria-label={`Usar ${color} como color principal`} onClick={() => set('primaryColor', color)} style={{
              backgroundColor: color
            }} className={`h-9 w-9 rounded-full border-2 transition-transform hover:scale-105 ${values.primaryColor === color ? 'border-foreground' : 'border-transparent'}`} data-id="element-1690" data-mp="3tmrjnqpevakb" />)}
              </div>
            </div>
            <div className="rounded-xl border border-border p-5" style={{
          borderColor: `${values.primaryColor}33`
        }} data-id="element-1691" data-mp="1myise58zfco8">
              <p className="text-xs uppercase tracking-wider text-muted-foreground" data-id="element-1692" data-mp="20t1ojyyc1jy3">
                Vista previa
              </p>
              <p className="mt-2 text-lg font-semibold" style={{
            color: values.secondaryColor
          }} data-id="element-1693" data-mp="2aouyrbokx1cp">
                {values.companyName || 'Tu empresa'}
              </p>
              <span className="mt-4 inline-flex h-10 items-center rounded-lg px-5 text-sm font-medium text-white" style={{
            backgroundColor: values.primaryColor
          }} data-id="element-1694" data-mp="gb3pida5wjhs">
                Botón principal
              </span>
            </div>
          </>}

        {step === 4 && <Select id="sector" label="Sector de la empresa" placeholder="Selecciona un sector" options={SECTOR_OPTIONS} value={values.sector} error={errors.sector} onChange={e => set('sector', e.target.value)} data-id="element-1695" data-mp="112isgxfpfmew" />}
      </div>

      <div className="mt-9 flex items-center justify-between gap-3" data-id="element-1696" data-mp="1ezny4wbiaj9m">
        <Button variant="ghost" disabled={step === 0} leftIcon={<ArrowLeftIcon className="h-4 w-4" data-id="element-1698" data-mp="1jxz6t3q2enq9" />} onClick={() => setStep(s => Math.max(0, s - 1))} data-id="element-1697" data-mp="1exunl5xrvewc">
          Anterior
        </Button>
        {step < STEPS.length - 1 ? <Button size="lg" rightIcon={<ArrowRightIcon className="h-4 w-4" data-id="element-1700" data-mp="1sr2e0ua4a4cg" />} onClick={() => {
        if (validateStep(step)) setStep(s => s + 1);
      }} data-id="element-1699" data-mp="1src56l6wehjq">
            Continuar
          </Button> : <Button size="lg" className="ava-glow" loading={loading} onClick={finish} data-id="element-1701" data-mp="1xs9mywnzx9kp">
            Crear mi CRM
          </Button>}
      </div>
    </AuthLayout>;
}